import { AxiosResponse } from "axios";
import { Dialog } from "components/Dialog";
import { API_URL_PROJECTS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { useFormik } from "formik";
import {
  ICreateProject,
  IProject,
  ProjectType,
  ProjectTypes
} from "interfaces/project";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDebounce } from "react-use";
import * as yup from "yup";

import { MenuItem, TextField } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UserDropdown } from "components/UserDropdown";
import { UsersContext } from "contexts/UsersContext";
import { DatePicker } from "components/DatePicker";
import { styled } from "@mui/material/styles";
import { MultilineTextField } from "components/MultilineTextField";
import { getCheckProjectNameExists } from "pages/Projects/getCheckProjectNameExists";
import { toISODate } from "core/date-helpers";
import { Dropdown } from "components/Dropdown";
import { UNASSIGNED } from "core/constants";

export interface ICreateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (project: IProject) => void;
}

const PROJECT_NAME_MAX_LENGTH = 500;

const ERROR_MESSAGE_PROJECT_NAME_EXISTS =
  "A project with the same name already exists.";

const PROJECT_DESCRIPTION_MAX_LENGTH = 2000;

const validationSchema = yup.object({
  name: yup
    .string()
    .nullable()
    .required("Project name is required")
    .trim()
    .min(3, "Project name must be at least 3 characters")
    .max(
      PROJECT_NAME_MAX_LENGTH,
      `Project name must be less than ${PROJECT_NAME_MAX_LENGTH} characters`
    )
    .matches(
      /^[a-zA-Z0-9\-\s]+$/,
      "Only alphanumeric characters, dashes and spaces allowed"
    ),
  projectType: yup.string().required("Project type is required"),
  dueDate: yup
    .date()
    .label("Due date")
    .nullable(),
  projectOwnerId: yup
    .number()
    .label("Owner")
    .min(0)
    .nullable(),
  projectDefaultActionAssgineeId: yup
    .number()
    .label("Default action assignee")
    .min(0)
    .nullable(),
  description: yup
    .string()
    .trim()
    .label("Description")
    .max(
      PROJECT_DESCRIPTION_MAX_LENGTH,
      `Project description must be less than ${PROJECT_DESCRIPTION_MAX_LENGTH} characters`
    )
});

interface ProjectFormValues {
  name: string;
  projectOwnerId: number | null;
  projectDefaultActionAssigneeId: number | null;
  projectType: ProjectType | "";
  dueDate: Date | null;
  description: string;
}

const initialValues: ProjectFormValues = {
  name: "",
  projectOwnerId: null,
  projectDefaultActionAssigneeId: null,
  projectType: "",
  dueDate: null,
  description: ""
};

type FormValuesType = typeof initialValues;

export const CreateProjectDialog: React.FC<ICreateProjectDialogProps> = ({
  isOpen,
  onClose,
  onConfirm
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const [debouncedProjectName, setDebouncedProjectName] = useState("");
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { nonServiceAccountUsers, internalUsers } = useContext(UsersContext);

  const checkProjectExists = useQuery<boolean>({
    queryKey: ["project-name-exists", debouncedProjectName],
    queryFn: () => getCheckProjectNameExists(axios, debouncedProjectName),
    cacheTime: 0,
    enabled: isOpen
  });

  const postCreateProject = useMutation(["create-project"], {
    mutationFn: (newProject: ICreateProject) =>
      axios
        .post<ICreateProject, AxiosResponse<IProject>>(
          API_URL_PROJECTS,
          newProject
        )
        .then(r => r.data)
  });

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValuesType,
      reset: () => void
    ): Promise<void> => {
      if (!isValid || checkProjectExists.isLoading) return;
      const exists = await getCheckProjectNameExists(axios, values.name);
      if (exists) {
        addSnackbarToQueue({
          message: ERROR_MESSAGE_PROJECT_NAME_EXISTS,
          severity: "error"
        });
      } else {
        try {
          const createProjectResponse = await postCreateProject.mutateAsync({
            name: values.name,
            project_type: values.projectType as ProjectType,
            owner: values.projectOwnerId,
            default_action_assignee: values.projectDefaultActionAssigneeId,
            target_completion_date: toISODate(values.dueDate) ?? undefined,
            description: values.description
          });
          addSnackbarToQueue({
            message: "New project was successfully created.",
            severity: "success"
          });
          onConfirm(createProjectResponse);
          reset();
        } catch (err) {
          addSnackbarToQueue({
            message: "Error creating project.",
            severity: "error"
          });
        }
      }
    },
    [
      addSnackbarToQueue,
      axios,
      checkProjectExists.isLoading,
      onConfirm,
      postCreateProject
    ]
  );

  const formik = useFormik<ProjectFormValues>({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, () => {
        setTimeout(() => {
          formik.setErrors({});
          formik.resetForm();
        }, 50);
      });
    }
  });

  const closeAndResetDialog = (): void => {
    onClose();
    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [,] = useDebounce(
    () => setDebouncedProjectName(formik.values.name),
    200,
    [formik.values.name]
  );

  const loading = postCreateProject.isLoading;

  const formHasErrors = !formik.isValid || checkProjectExists.data;

  return (
    <Dialog
      dialogId="dialog-create-project"
      title="Create Project"
      open={isOpen}
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="PP-Create-Project-Dialog"
      confirmDisabled={loading || formHasErrors}
      confirmText="Add"
      onSubmit={e => {
        e.preventDefault();
        formik.submitForm();
      }}
    >
      <StyledDialogContent>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          name="name"
          required
          id="projectName"
          label="Project Name"
          data-qa="PP-Text-Project-Name"
          inputProps={{ maxLength: PROJECT_NAME_MAX_LENGTH }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            (formik.touched.name && Boolean(formik.errors.name)) ||
            checkProjectExists.data
          }
          helperText={
            (formik.touched.name && formik.errors.name) ||
            (checkProjectExists.data && ERROR_MESSAGE_PROJECT_NAME_EXISTS)
          }
          value={formik.values.name}
        />
        <Dropdown
          fullWidth
          id="project-type-select"
          name="projectType"
          label="Project Type"
          required
          value={formik.values.projectType}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.projectType && Boolean(formik.errors.projectType)
          }
          helperText={
            formik.touched.projectType && formik.errors.projectType
              ? formik.errors.projectType
              : undefined
          }
        >
          {ProjectTypes.map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
        </Dropdown>
        <UserDropdown
          label="Owner"
          users={internalUsers}
          unassignedValue={UNASSIGNED}
          avatarTooltip="The owner of the project"
          selectedUserId={formik.values.projectOwnerId}
          onChangeSelectedUserId={newOwnerId =>
            formik.setFieldValue("projectOwnerId", newOwnerId)
          }
          error={
            Boolean(formik.touched.projectOwnerId) &&
            Boolean(formik.errors.projectOwnerId)
          }
          helperText={
            formik.touched.projectOwnerId
              ? formik.errors.projectOwnerId
              : undefined
          }
        />
        <UserDropdown
          label={`Default ${formik.values.projectType} Assignee`}
          users={nonServiceAccountUsers}
          unassignedValue={UNASSIGNED}
          avatarTooltip={`The user to be assigned to project ${formik.values.projectType.toLocaleLowerCase()} actions`}
          selectedUserId={formik.values.projectDefaultActionAssigneeId}
          onChangeSelectedUserId={newAssigneeId =>
            formik.setFieldValue(
              "projectDefaultActionAssigneeId",
              newAssigneeId
            )
          }
          error={
            Boolean(formik.touched.projectDefaultActionAssigneeId) &&
            Boolean(formik.errors.projectDefaultActionAssigneeId)
          }
          helperText={
            formik.touched.projectDefaultActionAssigneeId
              ? formik.errors.projectDefaultActionAssigneeId
              : undefined
          }
        />
        <DatePicker
          date={formik.values.dueDate}
          onChange={ev => formik.setFieldValue("dueDate", ev)}
          slotProps={{
            textField: {
              InputLabelProps: {
                shrink: true
              },
              onBlur: formik.handleBlur,
              error: formik.touched.dueDate && Boolean(formik.errors.dueDate),
              helperText: formik.touched.dueDate && formik.errors.dueDate
            }
          }}
          id="dueDate"
          label="Due Date"
          data-qa="PP-Text-Due-Date"
          ariaLabel="project-due-date-picker"
          clearable
          disableFuture={false}
          fullWidth
          size="medium"
        />
        <MultilineTextField
          name="description"
          id="projectDescription"
          label="Description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            (formik.dirty || formik.touched.description) &&
            Boolean(formik.errors.description)
          }
          helperText={
            (formik.dirty || formik.touched.description) &&
            formik.errors.description
          }
          data-qa="PP-Text-Project-Description"
          fullWidth
        />
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled("div")`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;
